import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { SelectedAsset, ViewMode } from "@/store/assetsExplorer";
import { RtcTask } from "@/store/thread";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { useMemo } from "react";
import { AssetItem } from "./shared/AssetItem";

interface ImageTabProps {
  data: ArtifactsViewerData["imageStoryboard"]["data"];
  rtcTasks: RtcTask[];
  viewMode: ViewMode;
  selectedFilePaths: Set<string>;
  onSelect: (asset: SelectedAsset) => void;
  stopSignal?: number;
}

export function ImageTab(props: ImageTabProps) {
  const { data, viewMode, selectedFilePaths, onSelect, stopSignal } = props;

  // Get all image versions from shotsMetadatas for each shot
  const images = useMemo(() => {
    const result: Array<{
      path: string;
      url: string;
      shotIndex: number;
    }> = [];

    data.shots.forEach((shot, index) => {
      const shotIndex = shot.shot_index;
      const metadata = data.shotsMetadatas[index];

      if (metadata?.start_frame_image?.versions) {
        const versions = metadata.start_frame_image.versions;
        [...versions].reverse().forEach((version) => {
          if (version.file_path) {
            result.push({
              path: version.file_path,
              url: convertFilePathToUrl(version.file_path, { level: "medium" }),
              shotIndex,
            });
          }
        });
      }
    });

    return result;
  }, [data.shots, data.shotsMetadatas]);

  // Always call useMemo for shotGroups (Hooks must be called in the same order)
  const shotGroups = useMemo(() => {
    const groups: Record<number, typeof images> = {};
    images.forEach((image) => {
      const key = image.shotIndex;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(image);
    });
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  }, [images]);

  if (images.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary">
        No images available
      </div>
    );
  }

  if (viewMode === "grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {images.map((image, index) => (
          <AssetItem
            key={`image_grid_${image.path}_${index}`}
            src={image.url}
            previewSrc={convertFilePathToUrl(image.path, { level: "medium" })}
            alt={`Shot ${image.shotIndex}`}
            mediaType="image"
            index={image.shotIndex}
            onClick={() => onSelect({ type: "image", path: image.path })}
            stopSignal={stopSignal}
          />
        ))}
      </div>
    );
  }

  // Shots List view - group by shot

  return (
    <div className="flex flex-col gap-8">
      {shotGroups.map(([shotIndex, shotImages]) => (
        <div key={shotIndex}>
          <h3 className="text-text-primary text-base font-bold mb-4">
            Shot {Number(shotIndex) + 1}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {shotImages.map((image) => (
              <AssetItem
                key={`image_list_${image.path}_${image.shotIndex}`}
                src={image.url}
                previewSrc={convertFilePathToUrl(image.path, {
                  level: "medium",
                })}
                alt={`Shot ${image.shotIndex}`}
                mediaType="image"
                isSelected={selectedFilePaths.has(image.path)}
                onClick={() => onSelect({ type: "image", path: image.path })}
                stopSignal={stopSignal}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
